import {
  PAIDOL_SESSION_ID_KEY,
  paidolIdFromGroups,
  refreshUserSession,
  savePaidolSessionId,
  signOut,
} from 'app/store/authSlice';
import { Auth } from 'aws-amplify';
import { paidolService } from 'app/services/paidol.service';
import { SnackbarUtils } from 'app/shared-components/util/SnackbarUtils';

export const RELOAD_DELAY_MS = 1500;
/** How long a sign-out toast stays up: no reload follows to cut it short. */
export const SIGN_OUT_TOAST_MS = 6000;

export interface SessionCheckOptions {
  /** true for the refresh trigger: a 401 (rotated session) is ignored (SEE-1760). */
  ignoreRotated?: boolean;
}

/** The locally stored `<paidolId>:<sessionId>`, split; either part may be missing. */
export function readLocalPaidolSession(): { paidolId?: string; sessionId?: string } {
  const paidolSessionId = localStorage.getItem(PAIDOL_SESSION_ID_KEY);
  return { paidolId: paidolSessionId?.split(':')[0], sessionId: paidolSessionId?.split(':')[1] };
}

// One check at a time across every caller: the Amplify middleware calls this
// once per refused request, so a page of refused queries would otherwise run a
// check, a toast and a sign-out each. A concurrent caller shares the pending
// check, and its own options are dropped.
let inFlight: Promise<void> | null = null;

export const isPaidolSessionCheckInFlight = (): boolean => inFlight !== null;

/**
 * SEE-1738: the session check run when a request is refused as Unauthorized.
 * Shared by the Amplify middleware (graphqlRequest) and CdkSessionGuard.
 * Never throws: the caller decides what to do with the original error.
 */
export function runPaidolSessionCheck(
  dispatch: (action: unknown) => unknown,
  options: SessionCheckOptions = {}
): Promise<void> {
  if (!inFlight) {
    inFlight = runCheck(dispatch, options).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function runCheck(dispatch: (action: unknown) => unknown, options: SessionCheckOptions): Promise<void> {
  const { paidolId, sessionId } = readLocalPaidolSession();
  // SEE-1456: only validate the session when one has actually been
  // established locally. During login boot the key isn't written yet,
  // and checkPaidolSession('','') is a guaranteed 401 — which is what
  // escalated SEE-1310's field-level SSO denials into a forced
  // sign-out loop for every SSO user. An Unauthorized with no local
  // session to check must propagate as an ordinary error, never end
  // the session.
  if (!sessionId || !paidolId) {
    return;
  }
  try {
    const result = await paidolService.checkPaidolSession({
      sessionId: sessionId || '',
      paidolId: paidolId || '',
    });
    if (result.statusCode === 401) {
      // A rotated session (a sign-in elsewhere replaced the row). The refresh
      // trigger ignores it so single-session is no stricter than today.
      if (!options.ignoreRotated) {
        dispatch(signOut());
      }
    } else if (result.statusCode === 403) {
      // SEE-1738: the user is no longer an active member of the company.
      SnackbarUtils.enqueue('You no longer have access to this company. Signing you out…', {
        variant: 'warning',
        autoHideDuration: SIGN_OUT_TOAST_MS,
      });
      dispatch(signOut());
    } else if (result.statusCode === 200) {
      // SEE-1583: the session row agrees with this browser, yet a
      // paidol-scoped read was refused. That is a token minted for a
      // different company than the one selected here: another device
      // switched companies, the row moved back, and this tab's JWT
      // still carries the other company's group. Every read of the
      // selected company fails until the token is re-minted, and the
      // transactions table would otherwise freeze on stale rows with
      // no message. Re-mint against the row; reload only once that
      // succeeded, since a reload with the same stale token would just
      // land back here (createAsyncThunk resolves to its rejected
      // action rather than throwing).
      const session = await Auth.currentSession();
      const jwtPaidolId = paidolIdFromGroups(session.getIdToken().payload['cognito:groups']);
      if (jwtPaidolId && jwtPaidolId !== paidolId) {
        const outcome = await (dispatch(refreshUserSession()) as unknown as Promise<{
          type: string;
        }>);
        if (!outcome.type.endsWith('/rejected')) {
          SnackbarUtils.enqueue('Your session was refreshed for the selected company. Reloading…', {
            variant: 'info',
            autoHideDuration: RELOAD_DELAY_MS,
          });
          setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
        }
      }
    } else if (result.statusCode === 421) {
      // Another tab/session rotated the SelectedPaidolSession row.
      // Surface a brief toast so the impending reload doesn't look
      // like the app silently flipped the user's active company.
      const session = await Auth.currentSession();
      const cognitoGroups = session.getIdToken().payload['cognito:groups'];
      // SSO tokens list the identity-provider group first, so [0] is
      // not the paidol for them; read the bare paidol id.
      const currentPaidolId = paidolIdFromGroups(cognitoGroups) ?? '';
      SnackbarUtils.enqueue('Your selected company was updated in another session. Refreshing…', {
        variant: 'info',
        autoHideDuration: RELOAD_DELAY_MS,
      });
      // Reload regardless of recovery-thunk outcome — local state
      // is already known to disagree with the server.
      try {
        await (dispatch(savePaidolSessionId(currentPaidolId)) as unknown as Promise<unknown>);
      } finally {
        setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
      }
    }
    // Any other status (e.g. 500, a fault in the check) leads to no action.
  } catch {
    // Session check failed — let original error propagate
  }
}
